import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { resolveDestinoFromFase } from '../utils/geocoding';
import type { FaseOS, OrdemServico } from '../types';
import LoadingScreen from '../components/LoadingScreen';

// Corrigir ícones padrão do Leaflet no Vite
const DefaultIcon = L.icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface MapLocationState {
  fase: FaseOS;
  ordemServico: OrdemServico;
}

interface RouteInfo {
  distanceKm: number;
  durationMin: number;
}

const OSRM_BASE = 'https://router.project-osrm.org/route/v1/driving';

const fetchRoute = async (
  from: { lat: number; lng: number },
  to: { lat: number; lng: number }
): Promise<{ coords: [number, number][]; distanceKm: number; durationMin: number } | null> => {
  const url = `${OSRM_BASE}/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const route = data.routes?.[0];
  if (!route?.geometry?.coordinates?.length) return null;
  const coords = route.geometry.coordinates.map((c: number[]) => [c[1], c[0]] as [number, number]);
  return {
    coords,
    distanceKm: (route.distance ?? 0) / 1000,
    durationMin: (route.duration ?? 0) / 60,
  };
};

const getPosicaoAtual = (): Promise<GeolocationPosition | null> =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 60000,
    });
  });

const MapPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as MapLocationState | null;
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const routeLineRef = useRef<L.Polyline | null>(null);
  const [origem, setOrigem] = useState<{ lat: number; lng: number } | null>(null);
  const [destino, setDestino] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);

  useEffect(() => {
    if (!state?.fase?.endereco_destino) {
      setLoading(false);
      setError('Dados da fase não disponíveis.');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const [position, coordsDestino] = await Promise.all([
          getPosicaoAtual(),
          resolveDestinoFromFase(state.fase),
        ]);

        if (cancelled) return;
        if (!coordsDestino) {
          setError('Não foi possível localizar o destino com confiança.');
        } else {
          setDestino(coordsDestino);
        }
        if (position?.coords) {
          setOrigem({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        }
      } catch (err) {
        if (!cancelled) setError('Não foi possível carregar o mapa.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [state?.fase?.endereco_destino]);

  useEffect(() => {
    if (loading || error || !mapRef.current || !destino) return;

    const center = origem ?? destino;
    const map = L.map(mapRef.current).setView([center.lat, center.lng], 13);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
    }).addTo(map);

    L.marker([destino.lat, destino.lng])
      .addTo(map)
      .bindPopup(`<strong>Destino</strong><br/>${state?.fase?.endereco_destino ?? ''}`);

    if (origem) {
      L.marker([origem.lat, origem.lng])
        .addTo(map)
        .bindPopup('<strong>Você</strong><br/>Posição atual');
      const bounds = L.latLngBounds(
        [origem.lat, origem.lng],
        [destino.lat, destino.lng]
      );
      map.fitBounds(bounds, { padding: [40, 40] });

      setRouteLoading(true);
      fetchRoute(origem, destino)
        .then((route) => {
          if (!route || !mapInstanceRef.current) return;
          if (routeLineRef.current) {
            mapInstanceRef.current.removeLayer(routeLineRef.current);
          }
          const line = L.polyline(route.coords, {
            color: '#2563eb',
            weight: 5,
            opacity: 0.8,
          });
          line.addTo(mapInstanceRef.current);
          routeLineRef.current = line;
          mapInstanceRef.current.fitBounds(line.getBounds(), { padding: [40, 40] });
          setRouteInfo({ distanceKm: route.distanceKm, durationMin: route.durationMin });
        })
        .catch(() => setRouteInfo(null))
        .finally(() => setRouteLoading(false));
    }

    return () => {
      if (routeLineRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(routeLineRef.current);
        routeLineRef.current = null;
      }
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [loading, error, origem, destino, state?.fase?.endereco_destino]);

  const handleVoltar = () => {
    navigate(-1);
  };

  if (!state?.fase) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-600 mb-4">Dados da fase não disponíveis.</p>
        <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard')}>
          Voltar ao Dashboard
        </button>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen message="Carregando mapa..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-600 mb-4">{error}</p>
        <button type="button" className="btn-secondary" onClick={handleVoltar}>
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-300 shrink-0">
        <div className="p-3 flex items-center justify-between">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleVoltar}
          >
            Voltar
          </button>
          <h1 className="text-lg font-semibold text-gray-900 truncate flex-1 text-center mx-2">
            {state.fase.nome_fase}
          </h1>
          <div style={{ width: '70px' }} />
        </div>
        {(routeLoading || routeInfo) && (
          <div className="px-3 pb-2 pt-0 text-sm text-gray-600 border-t border-gray-100">
            {routeLoading ? (
              <span>Calculando rota...</span>
            ) : routeInfo ? (
              <span>
                <strong>Distância:</strong> {routeInfo.distanceKm.toFixed(1)} km
                {' • '}
                <strong>Tempo:</strong> ~{Math.round(routeInfo.durationMin)} min
              </span>
            ) : null}
          </div>
        )}
      </header>
      <div
        ref={mapRef}
        className="flex-1 w-full min-h-[300px]"
        style={{ minHeight: 'calc(100vh - 56px)' }}
      />
    </div>
  );
};

export default MapPage;
